import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import type { PaymentAccount } from "~/shared/schemas/invoice-schema.ts";
import { paymentAccountSchema } from "~/shared/schemas/invoice-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const PayAccountPayloadSchema = z.object({
	paymentMethodNonce: z.string(),
	makeDefault: z.boolean(),
	paymentMethod: z.number(), // 1 for ACH, 2 for CC (verify this with user/API)
	cardholderName: z.string().optional(),
	accountHolderName: z.string().optional(),
	billingAddress: z.object({
		address1: z.string(),
		address2: z.string().optional().nullable(),
		city: z.string(),
		state: z.string(),
		postalCode: z.string(),
		countryCode: z.string(),
	}),
});

export type PayAccountPayload = z.infer<typeof PayAccountPayloadSchema>;

export const postPayAccount = async (
	payload: PayAccountPayload,
	signal?: AbortSignal,
): Promise<PaymentAccount> => {
	const resp = await postAndValidate(
		apiRoutes.POSTPAYACCOUNT,
		payload,
		paymentAccountSchema as z.ZodType<PaymentAccount>,
		{
			signal,
		},
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostPayAccount = () => {
	const queryClient = useQueryClient();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		PaymentAccount,
		Error,
		PayAccountPayload
	>({
		mutationFn: (payload: PayAccountPayload) => postPayAccount(payload),
		onSuccess: () => {
			// Option 1: Manual update (Optimistic/Direct update)
			// You can use queryClient.setQueryData to update the list immediately
			// if the `data` returned from the server contains the new account info.
			/*
			queryClient.setQueryData(["customerAccounts", customerId], (oldData: any) => {
				if (!oldData) return oldData;
				return {
					...oldData,
					paymentAccounts: [...oldData.paymentAccounts, data]
				};
			});
			*/

			// Option 2: Invalidation (Reliable)
			// This tells React Query that the accounts data is stale and needs to be re-fetched.
			queryClient.invalidateQueries({
				queryKey: ["customerAccounts"],
			});

			// If you use multiple queries for payment methods, invalidate them all:
			queryClient.invalidateQueries({
				queryKey: ["customerPaymentMethods"],
			});
		},
	});

	return {
		savePaymentMethod: mutate,
		savePaymentMethodAsync: mutateAsync,
		isSaving: isPending,
		error,
		data,
		resetError: reset,
	};
};
